<template>
  <div v-if="showContent && lastCheckpoint" class="dropdown-content">
    <EWave class="dropdown-content__wave" aria-hidden="true" />
    <EIcon
      class="dropdown-content__indicator"
      icon="highlight-star"
      size="s"
    />
    <div class="dropdown-content__section">
      <div class="dropdown-content__header">
        <p
          class="dropdown-content__text"
          data-test-id="dropdown-content-text"
        >
          {{ text }}
        </p>
        <EButton
          icon="close"
          icon-size="s"
          size="s"
          variant="bare"
          @click="onClose"
        />
      </div>
      <a
        :href="videoLink"
        target="_blank"
        rel="noopener"
        data-test-id="dropdown-experiment-content-cta"
        @click="onClick"
      >
        <img
          :src="imgPath"
          :alt="altText"
          width="213"
          height="119"
          class="dropdown-content__img"
        >
      </a>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import {
  PERSONAL_COUNTER_CONTENT_SEARCH as contentSearchStorageKey,
  PERSONAL_COUNTER_CONTENT_HALF_TREE as contentHalfTreeStorageKey,
  PERSONAL_COUNTER_CONTENT_TREE as ContentTreeStorageKey,
} from '~~/common/js/constants/storage-keys';
import { CHECKPOINTS } from '~~/common/js/constants/search';
import { sendEvent, sendCoreAnalyticsEvent } from '~~/common/js/client/analytics';
import EButton from '~~/common/vue/components/button';
import EIcon from '~~/common/vue/components/icon';
import EWave from './wave.svg';

export default {
  name: 'EDropdownExperimentContent',
  components: {
    EButton,
    EIcon,
    EWave,
  },
  constants: {
    checkpointInput: {
      [CHECKPOINTS.FIRST_SEARCH]: {
        text: 'Wondering how we turn searches into trees? Watch to find out:',
        videoLink: 'https://www.youtube.com/watch?v=yRDA1ynrHTU',
        storageKey: contentSearchStorageKey,
        altText: 'Before and after picture of planting trees',
      },
      [CHECKPOINTS.FIRST_HALF_TREE_PLANTED]: {
        text: 'Keep the searches coming! See the impact our community is having:',
        videoLink: 'https://www.youtube.com/watch?v=uKdtGE_eLQo',
        storageKey: contentHalfTreeStorageKey,
        altText: 'Impact of our communities before and after',
      },
      [CHECKPOINTS.FIRST_TREE_PLANTED]: {
        text: 'Every tree counts! Don’t take it from us, take it from Marcela:',
        videoLink: 'https://www.youtube.com/watch?v=M0TotM68INU',
        storageKey: ContentTreeStorageKey,
        altText: 'Farmer inspecting trees in Brazil',
      },
    },
  },
  data() {
    return {
      showContent: false,
    };
  },
  computed: {
    ...mapGetters('user', ['lastCheckpoint']),
    ...mapState('user', ['treeCount']),
    imgPath() {
      // eslint-disable-next-line import/no-dynamic-require, global-require
      return require(`~~/common/vue/assets/images/personal-counter-dropdown-video-${this.lastCheckpoint}.png`);
    },
    videoLink() {
      return this.checkpointInput[this.lastCheckpoint].videoLink;
    },
    text() {
      return this.checkpointInput[this.lastCheckpoint].text;
    },
    altText() {
      return this.checkpointInput[this.lastCheckpoint].altText;
    },
    contentStorageKey() {
      return this.lastCheckpoint ? this.checkpointInput[this.lastCheckpoint].storageKey : null;
    },
  },
  mounted() {
    if (localStorage) {
      this.showContent = !localStorage.getItem(this.contentStorageKey);
    }
    this.unwatch = this.$watch('showContent', (val) => {
      this.$store.commit('user/setIsDropdownContentVisible', val);
    }, { immediate: true });
  },
  beforeDestroy() {
    if (this.unwatch) {
      this.unwatch();
    }
  },
  methods: {
    sendEvent(name, legacyName) {
      const payload = { treeCount: Number(this.treeCount), checkpoint: this.lastCheckpoint };
      sendEvent(legacyName, payload);
      sendCoreAnalyticsEvent(name, payload);
    },
    onClick() {
      this.sendEvent('personalCounterClickDropdownContent', 'personal-counter-dropdown-content-click');
    },
    onClose() {
      this.showContent = false;
      if (this.contentStorageKey && localStorage) {
        localStorage.setItem(this.contentStorageKey, '1');
      }
      this.sendEvent('personalCounterCloseDropdownContent', 'personal-counter-dropdown-content-close');
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-content {
  position: relative;
}

.dropdown-content__wave {
  width: 100%;
  color: var(--color-background-elevation-1);
  background-color: var(--color-background-secondary);
}

.dropdown-content__indicator {
  color: $c-red-30;
  position: absolute;
  left: 11px;
  top: 15px;
  transform: scale(1.6);
}

.dropdown-content__section {
  background: var(--color-background-secondary);
  padding: $space-s $space-m $space-m;
}

.dropdown-content__header {
  display: flex;
  justify-content: space-between;
}

.dropdown-content__text {
  margin-top: $space-2s;
}

.dropdown-content__img {
  border-radius: $border-radius-l;
  transition: box-shadow $timing-2s $easing;
  will-change: box-shadow;
}

.dropdown-content__img:hover,
.dropdown-content__img:focus {
  @include elevation(2);
}
</style>
